import React, { useEffect, useState } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  DatePicker,
  Table,
  Tag,
  Avatar,
  Modal,
  Upload,
  Button,
  Popconfirm
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { faClipboardCheck, faEye, faPencil, faPlus, faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'antd/es/typography/Link';
import { userAtom, notificationsAtom, userPrivilagesAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1, H4 } from '../../shared-components/typography/Title';
import api from '../../../api/api';
import {
  fareboxCollectionSubmissionFilters,
  fareboxCollectionSubmissionAttachmentFileTypes,
  fareboxCollectionSubmissionStatus
} from '../../../utils';
import {
  fareboxCollectionSubmissionStatusToString,
  fareboxCollectionSubmissionAttachmentFileTypesToString,
  toCurrency
} from '../../../functions';
import Select from '../../shared-components/form/Select';
import CustomButton from '../../shared-components/buttons/Button';
import Form from '../../shared-components/form/Form';
import DateSelector from '../../shared-components/form/DatePicker';
import Input from '../../shared-components/form/Input';

function FareboxCollectionSubmissions() {
  const { Dragger } = Upload;
  const user = useRecoilValue(userAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);

  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sumbissionsFilter, setSumbissionsFilter] = useState(0);
  const [selectedFileType, setSelectedFileType] = useState(0);
  const [fileData, setFileData] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const draggerProps = {
    name: 'file',
    accept: 'image/*,application/pdf',
    multiple: false,
    showUploadList: true,
    maxCount: 1,
    fileList: fileData ? [fileData] : [],
    beforeUpload: (file) => {
      setFileData(file);
      return false;
    },
    ondrop: (e) => {
    },
    onRemove: (e) => {
      setFileData(null);
    }
  };

  function getStatusColor(status) {
    if (status === 2) {
      return 'orange';
    }
    if (status === 3) {
        return 'green';
      }
    if (status === 4) {
      return 'red';
    }
    return '';
  }

  function getSubmissions(selectedPage) {
    if (!selectedDate) {
      return;
    }
    setCurrentPage(selectedPage);
    const startDate = dayjs(selectedDate);
    const endDate = startDate.endOf('month');
    setLoading(true);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    api.post('sonnell/farebox/submissions/all', {
      StartDate: startString,
      EndDate: endString,
      Page: currentPage,
      Size: pageSize,
      Filter: sumbissionsFilter
    })
    .then((response) => {
      setLoading(false);
      if (response.data.success) {
        setSubmissions(response.data.data.submissions);
        setTotalSubmissions(response.data.data.total);
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setLoading(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    });
  }

  function updateSubmission(formData) {
    setSubmissionLoading(true);
    api.put('sonnell/farebox/submission', {
      ...formData,
      resubmit: formData.resubmit ?? null,
      collectionDate: dayjs(formData.collectionDate),
      depositDate: dayjs(formData.depositDate)
    })
      .then((response) => {
        setSubmissionLoading(false);
        if (response.data.success) {
          setNotifications([
            ...notifications,
            {
              title: t('yay'),
              description: response.data.message,
              error: false,
            },
          ]);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
        getSubmissions(currentPage);
      })
      .catch((error) => {
        setSubmissionLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function createSubmission(formData) {
    setSubmissionLoading(true);
    api.post('sonnell/farebox/submission', {
      ...formData,
      collectionDate: dayjs(formData.collectionDate),
      depositDate: dayjs(formData.depositDate)
    })
      .then((response) => {
        setSubmissionLoading(false);
        if (response.data.success) {
          setNotifications([
            ...notifications,
            {
              title: t('yay'),
              description: response.data.message,
              error: false,
            },
          ]);
          submissionData.setFieldValue('id', response.data.data.id);
          submissionData.setFieldValue('submitter', response.data.data.submitter);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
        getSubmissions(currentPage);
      })
      .catch((error) => {
        setSubmissionLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function reviewSubmission(formData) {
    setSubmissionLoading(true);
    api.post('sonnell/farebox/submission/review', formData)
      .then((response) => {
        setSubmissionLoading(false);
        if (response.data.success) {
          setNotifications([
            ...notifications,
            {
              title: t('yay'),
              description: response.data.message,
              error: false,
            },
          ]);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
        getSubmissions(currentPage);
      })
      .catch((error) => {
        setSubmissionLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  const submissionData = useFormik({
    initialValues: {
      id: null,
      resubmit: true,
      collectionDate: dayjs().toString(),
      depositDate: dayjs().toString(),
      depositedBy: '',
      description: '',
      comments: '',
      collectedAmount: 0,
      depositedAmount: 0,
      adjustment: 0,
      isNew: true,
      isReadOnly: false,
      openModal: false
    },
    validationSchema: Yup.object().shape({
      depositedBy: Yup.string().required(t('deposited_by_name_required')),
      description: Yup.string().required(t('description_required')),
      collectedAmount: Yup.number().positive().min(0).required(t('positive_amount_required')),
      depositedAmount: Yup.number().positive().min(0).required(t('positive_amount_required'))
    }),
    onSubmit: (values, { setSubmitting }) =>
      values.id !== null ? updateSubmission(values) : createSubmission(values),
  });

  const submissionDataForReview = useFormik({
    initialValues: {
      id: null,
      collectionDate: dayjs().toString(),
      depositDate: dayjs().toString(),
      depositedBy: '',
      description: '',
      comments: '',
      collectedAmount: 0,
      depositedAmount: 0,
      adjustment: 0,
      openModal: false,
      reviewerComments: '',
      status: 0,
      attachments: []
    },
    validationSchema: Yup.object().shape({
      status: Yup.number().positive().min(2, t('select_valid_status'))
      .required(t('select_valid_status'))
      .test('statusCheck', t('select_valid_status'), (val) => val !== submissionDataForReview.values.originalStatus),
    }),
    onSubmit: (values, { setSubmitting }) => reviewSubmission(values),
  });

  function getAttachments(submissionId) {
    setLoadingAttachments(true);
    api.get(`sonnell/farebox/submission/attachments/${submissionId}`)
      .then((response) => {
        setLoadingAttachments(false);
        if (response.data.success) {
          setAttachments(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoadingAttachments(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function uploadFile() {
    const formdata = new FormData();
    formdata.append('file', fileData);
    formdata.append('submissionId', submissionData?.values?.id);
    formdata.append('uploadedFileName', fileData.name);
    formdata.append('fileType', selectedFileType);
    setUploadingFile(true);
    api.post('sonnell/farebox/submission/attachment', formdata)
      .then((response) => {
        setUploadingFile(false);
        if (response.data.success) {
          setFileData(null);
          getAttachments(submissionData?.values?.id);
          if (response.data.message) {
            setNotifications([
              ...notifications,
              {
                title: t('yay'),
                description: response.data.message,
                error: false,
              }
            ]);
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('yay'),
                description: t('uploaded_file_successfully'),
                error: false,
              }
            ]);
          }
        } else if (response.data.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: t('error_uploading_file'),
              error: true,
            }
          ]);
        }
        setFileData(null);
      })
      .catch((error) => {
        setUploadingFile(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function deleteFile(id) {
    api.delete(`sonnell/farebox/submission/attachment/${id}`)
      .then((response) => {
        if (response.data.success) {
          getAttachments(submissionData?.values?.id);
          if (response.data.message) {
            setNotifications([
              ...notifications,
              {
                title: t('yay'),
                description: response.data.message,
                error: false,
              }
            ]);
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('yay'),
                description: t('file_removed_successfully'),
                error: false,
              }
            ]);
          }
        } else if (response.data.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: t('error_deleting_file'),
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setUploadingFile(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  useEffect(() => {
    setSelectedDate(dayjs().format('MMM YYYY'));
  }, []);

  useEffect(() => {
    getSubmissions(0);
  }, [pageSize, sumbissionsFilter, selectedDate]);

  return (
    <MainPage>
      <H1 text={`Sonnell - ${t('farebox_collection_submissions')}`} />
      <div>
        <div className="w-full mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2">
          <Select
            className="col-span-1 lg:col-start-2 xl:col-start-3 mt-2"
            disabled={loading}
            label={t('filter')}
            id="filter"
            name="filter"
            value={sumbissionsFilter}
            onChange={(obj) => {
              setSumbissionsFilter(parseInt(obj.target.value, 10));
            }}
            options={_.map(fareboxCollectionSubmissionFilters, (f) => ({
            key: f.id,
            value: f.id,
            text: t(f.name),
            }))}
          />
          <div className="col-span-1">
            <DatePicker
              disabled={loading}
              picker="month"
              className="w-full h-11 mt-2 bg-transparent"
              value={dayjs(selectedDate)}
              onChange={(obj) => {
                setSelectedDate(obj.format('MMM YYYY'));
              }}
              format="MMM YYYY"
            />
          </div>
          <CustomButton
            className="bg-primaryColor col-span-1 md:col-span-2 lg:col-span-1 xl:col-start-0 mt-2"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            loading={loading}
            text={t('refresh')}
            onClick={() => getSubmissions(currentPage)}
          />
        </div>
        <div className="mt-2">
          <Table
            bordered
            locale={{
              emptyText: t('no_data')
            }}
            title={() => (
              <div className="flex">
                <CustomButton
                  className="bg-primaryColor px-2 ml-auto"
                  textColor="text-primaryTextColor"
                  loaderColor="bg-primaryTextColor"
                  borderColor="border-primaryBorderColor"
                  text={(
                    <div>
                      <FontAwesomeIcon className="mr-2" icon={faPlus} />
                      {t('create_new')}
                    </div>
                  )}
                  loading={loading}
                  onClick={() => {
                    setSelectedFileType(0);
                    submissionData.setFieldValue('id', null);
                    submissionData.setFieldValue('collectionDate', dayjs().toString());
                    submissionData.setFieldValue('depositDate', dayjs().toString());
                    submissionData.setFieldValue('depositedBy', '');
                    submissionData.setFieldValue('description', '');
                    submissionData.setFieldValue('comments', '');
                    submissionData.setFieldValue('collectedAmount', 0);
                    submissionData.setFieldValue('depositedAmount', 0);
                    submissionData.setFieldValue('isNew', true);
                    submissionData.setFieldValue('isReadOnly', false);
                    submissionData.setFieldValue('openModal', true);
                    submissionData.setFieldValue('adjustment', 0);
                    submissionData.setFieldValue('submitter', null);
                    submissionData.setFieldValue('reviewer', null);
                    submissionData.setFieldValue('submittedBy', '');
                    submissionData.setFieldValue('reviewedByUserId', '');
                    submissionData.setFieldValue('reviewerComments', '');
                    submissionData.setFieldValue('status', 0);
                    submissionData.setFieldValue('resubmit', false);
                    setAttachments([]);
                  }}
                />
              </div>
            )}
            rowKey="id"
            loading={loading}
            className="mt-2"
            pagination={{
              pageSize,
              showSizeChanger: true,
              current: currentPage + 1,
              total: totalSubmissions,
              onChange: (page, size) => {
                if (page - 1 !== currentPage) {
                  setCurrentPage(page - 1);
                  getSubmissions(page - 1);
                } else {
                  setPageSize(size);
                }
              }
            }}
            dataSource={submissions}
            columns={[
              {
                title: t('description'),
                align: 'center',
                className: 'text-sm',
                render: (row, object, index) => (
                  <span>{row.description}</span>
                ),
              },
              {
                title: t('submitted_on'),
                align: 'center',
                className: 'text-sm',
                render: (row, object, index) => (
                  <span>{dayjs(row.createdAt).format('MMM DD, YYYY hh:mm A')}</span>
                ),
              },
              {
                title: t('submitted_by'),
                align: 'center',
                className: 'text-sm',
                render: (row, object, index) => (
                <>
                  {row.submitter ?
                    <div>
                      <Avatar src={row.submitter?.avatar} />
                      <div>{row.submitter?.completeName}</div>
                    </div> : <span>-</span>}
                </>),
              },
              {
                title: t('attachments'),
                align: 'center',
                className: 'text-sm',
                render: (row, object, index) => (
                  <span>{row.attachments?.length ?? '-'}</span>
                ),
              },
              {
                title: t('status'),
                align: 'center',
                className: 'text-sm',
                render: (row, object, index) => (
                  <Tag color={getStatusColor(row.status)}>
                    {t(fareboxCollectionSubmissionStatusToString(row.status))}
                  </Tag>
                ),
              },
              {
                title: t('actions'),
                align: 'center',
                className: 'text-sm',
                render: (row, object, index) => (
                  <div className="flex justify-center space-x-2">
                    {user.id === row.submittedBy && (row.status === 0 || row.status === 2) && (
                      <CustomButton
                        className="bg-primaryColor px-2"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faPencil} />
                            {t('edit')}
                          </div>
                        )}
                        loading={loading}
                        onClick={() => {
                          setSelectedFileType(0);
                          submissionData.setFieldValue('id', row.id);
                          submissionData.setFieldValue('collectionDate', row.collectionDate);
                          submissionData.setFieldValue('depositDate', row.depositDate);
                          submissionData.setFieldValue('depositedBy', row.depositedBy);
                          submissionData.setFieldValue('description', row.description);
                          submissionData.setFieldValue('comments', row.comments ?? '');
                          submissionData.setFieldValue('collectedAmount', row.collectedAmount);
                          submissionData.setFieldValue('depositedAmount', row.depositedAmount);
                          submissionData.setFieldValue('isNew', false);
                          submissionData.setFieldValue('isReadOnly', false);
                          submissionData.setFieldValue('status', row.status);
                          submissionData.setFieldValue('openModal', true);
                          submissionData.setFieldValue('adjustment', row.adjustment);
                          submissionData.setFieldValue('submitter', row.submitter);
                          submissionData.setFieldValue('reviewer', row.reviewer);
                          submissionData.setFieldValue('reviewerComments', row.reviewerComments ?? '');
                          submissionData.setFieldValue('resubmit', false);
                          setAttachments([]);
                          getAttachments(row.id);
                        }}
                      />
                    )}
                    {(user.id !== row.submittedBy ||
                      ((row.status === 1 && !userPrivilages.hasOperatorManagerPrivilages) ||
                      row.status === 3 || row.status === 4)) && (
                      <CustomButton
                        className="bg-primaryColor px-2"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faEye} />
                            {t('view')}
                          </div>
                        )}
                        loading={loading}
                        onClick={() => {
                          submissionData.setFieldValue('id', row.id);
                          submissionData.setFieldValue('collectionDate', row.collectionDate);
                          submissionData.setFieldValue('depositDate', row.depositDate);
                          submissionData.setFieldValue('depositedBy', row.depositedBy);
                          submissionData.setFieldValue('description', row.description);
                          submissionData.setFieldValue('comments', row.comments ?? '');
                          submissionData.setFieldValue('collectedAmount', row.collectedAmount);
                          submissionData.setFieldValue('depositedAmount', row.depositedAmount);
                          submissionData.setFieldValue('reviewerComments', row.reviewerComments ?? '');
                          submissionData.setFieldValue('isNew', false);
                          submissionData.setFieldValue('status', row.status);
                          submissionData.setFieldValue('isReadOnly', true);
                          submissionData.setFieldValue('openModal', true);
                          submissionData.setFieldValue('adjustment', row.adjustment);
                          submissionData.setFieldValue('submitter', row.submitter);
                          submissionData.setFieldValue('reviewer', row.reviewer);
                          submissionData.setFieldValue('resubmit', false);
                          setAttachments([]);
                          getAttachments(row.id);
                        }}
                      />
                    )}
                    {((row.status === 0 || row.status === 1) &&
                      userPrivilages.hasOperatorManagerPrivilages) && (
                      <CustomButton
                        className="bg-primaryColor px-2"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={(
                          <div>
                            <FontAwesomeIcon className="mr-2" icon={faClipboardCheck} />
                            {t('review')}
                          </div>
                        )}
                        loading={loading}
                        onClick={() => {
                          if (row.status === 0) {
                            setSubmissionLoading(true);
                            api.post('sonnell/farebox/submission/review', {
                              ...row,
                              status: 1
                            })
                              .then((response) => {
                                setSubmissionLoading(false);
                                if (response.data.success) {
                                  const tempSubmissions = [...submissions];
                                  for (let i = 0; i < tempSubmissions.length; i++) {
                                    if (tempSubmissions[i].id === row.id) {
                                      tempSubmissions[i].status = 1;
                                      submissionDataForReview.setFieldValue('status', 1);
                                      submissionDataForReview.setFieldValue('originalStatus', 1);
                                    }
                                  }
                                  setSubmissions(tempSubmissions);
                                }
                              })
                              .catch((error) => {
                                setSubmissionLoading(false);
                              });
                          }

                          submissionDataForReview.setFieldValue('id', row.id);
                          submissionDataForReview.setFieldValue('collectionDate', row.collectionDate);
                          submissionDataForReview.setFieldValue('depositDate', row.depositDate);
                          submissionDataForReview.setFieldValue('depositedBy', row.depositedBy);
                          submissionDataForReview.setFieldValue('description', row.description);
                          submissionDataForReview.setFieldValue('comments', row.comments ?? '');
                          submissionDataForReview.setFieldValue('collectedAmount', row.collectedAmount);
                          submissionDataForReview.setFieldValue('depositedAmount', row.depositedAmount);
                          submissionDataForReview.setFieldValue('openModal', true);
                          submissionDataForReview.setFieldValue('adjustment', row.adjustment);
                          submissionDataForReview.setFieldValue('reviewerComments', row.reviewerComments ?? '');
                          submissionDataForReview.setFieldValue('status', row.status);
                          submissionDataForReview.setFieldValue('originalStatus', row.status);
                          submissionDataForReview.setFieldValue('submitter', row.submitter);
                          submissionDataForReview.setFieldValue('reviewer', row.reviewer);
                          setAttachments([]);
                          getAttachments(row.id);
                        }}
                      />
                    )}
                  </div>
                )
              }
            ]}
          />
        </div>
      </div>
      <Modal
        key="selectedSubmissionModal"
        title={submissionData?.values?.id ? (submissionData?.values?.isReadOnly ? t('view_submission') : t('edit_submission')) : t('new_submission')}
        open={submissionData?.values?.openModal}
        onCancel={() => submissionData.setFieldValue('openModal', false)}
        centered
        footer={[
          <div className="flex space-x-2">
            <div className="mr-auto" />
            <CustomButton
              key="closeSelectedSubmissionModal"
              type="primary"
              danger
              className="bg-rose-500 px-4"
              textColor="text-primaryTextColor"
              loaderColor="bg-primaryTextColor"
              disabled={submissionLoading}
              onClick={() => submissionData.setFieldValue('openModal', false)}
              text={t('cancel')}
            />
            {!submissionData?.values?.isReadOnly && submissionData?.values?.id && (
              <CustomButton
                className="bg-primaryColor px-4"
                textColor="text-primaryTextColor"
                loaderColor="bg-primaryTextColor"
                borderColor="border-primaryBorderColor"
                loading={submissionLoading}
                text={t('update')}
                onClick={submissionData.submitForm}
              />
            )}
            {!submissionData?.values?.isReadOnly &&
              submissionData?.values?.id && submissionData.values?.status === 2 && (
              <CustomButton
                className="bg-primaryColor px-4"
                textColor="text-primaryTextColor"
                loaderColor="bg-primaryTextColor"
                borderColor="border-primaryBorderColor"
                loading={submissionLoading}
                text={t('resubmit')}
                onClick={() => {
                  submissionData.setFieldValue('resubmit', true);
                  setTimeout(() => {
                    submissionData.submitForm();
                  }, 1000);
                }}
              />
            )}
            {!submissionData?.values?.id && submissionData?.values?.isNew && (
              <CustomButton
                className="bg-primaryColor px-4"
                textColor="text-primaryTextColor"
                loaderColor="bg-primaryTextColor"
                borderColor="border-primaryBorderColor"
                loading={submissionLoading}
                text={t('submit')}
                onClick={submissionData.submitForm}
              />
            )}
          </div>
        ]}
      >
        <Form className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full overflow-y-auto">
          {submissionData?.values?.submitter &&
            submissionData?.values?.submitter?.id !== user?.id && (
            <div className="md:col-span-2 space-x-1 flex">
              <strong className="mt-1">{t('submitted_by')}:</strong>
              <Avatar src={submissionData?.values?.submitter?.avatar} />
              <div className="mt-1">{submissionData?.values?.submitter?.completeName}</div>
            </div>
          )}
          {submissionData?.values?.isReadOnly && (
            <>
              <div className="md:col-span-2">
                <strong>{t('description')}</strong>
                <p>{submissionData?.values.description}</p>
              </div>
              <div>
                <strong>{t('amount_collected')}</strong>
                <p>{toCurrency(submissionData?.values.collectedAmount)}</p>
              </div>
              <div>
                <strong>{t('collection_date')}</strong>
                <p>{dayjs(submissionData?.values.collectionDate).format('MMM d, 2024')}</p>
              </div>
              <div>
                <strong>{t('amount_deposited')}</strong>
                <p>{toCurrency(submissionData?.values.depositedAmount)}</p>
              </div>
              <div>
                <strong>{t('deposit_date')}</strong>
                <p>{dayjs(submissionData?.values.depositDate).format('MMM d, 2024')}</p>
              </div>
              <div className="md:col-span-2">
                <strong>{t('deposited_by')}</strong>
                <p>{submissionData?.values.depositedBy}</p>
              </div>
              <div className="md:col-span-2">
                <strong>{t('comments')}</strong>
                {submissionData?.values.comments && (
                  <p>{submissionData?.values.comments}</p>
                )}
                {!submissionData?.values.comments && (
                  <p className="italic text-gray-400">{t('no_comments')}</p>
                )}
              </div> 
            </>
          )}
          {!submissionData?.values?.isReadOnly && (
            <>
              <Input
                label={t('description')}
                onChange={submissionData.handleChange}
                value={submissionData?.values.description}
                disabled={submissionData?.values?.isReadOnly || submissionLoading}
                required
                className="md:col-span-2"
                type="text"
                name="description"
                id="description"
                error={
                  submissionData.errors.description &&
                  submissionData.touched.description &&
                  submissionData.errors.description
                }
              />
              <Input
                label={t('amount_collected')}
                onChange={(obj) => {
                  // submissionData.handleChange
                  submissionData.setFieldValue('collectedAmount', obj.target.value);
                  const tempValue = parseFloat(obj.target.value, 10);
                  submissionData.setFieldValue('adjustment', (-1 * (tempValue - parseFloat(submissionData.values.depositedAmount)).toFixed(2)));
                }}
                value={submissionData?.values.collectedAmount}
                required
                disabled={submissionData?.values?.isReadOnly || submissionLoading}
                min={0}
                step={1}
                type="number"
                name="collectedAmount"
                id="collectedAmount"
                error={
                  submissionData.errors.collectedAmount &&
                  submissionData.touched.collectedAmount &&
                  submissionData.errors.collectedAmount
                }
              />
              <DateSelector
                label={t('collection_date')}
                maxDate={dayjs().toDate()}
                value={Date.parse(submissionData.values.collectionDate)}
                disabled={submissionData.values.isReadOnly || submissionLoading}
                error={
                  submissionData.errors.collectionDate &&
                  submissionData.touched.collectionDate &&
                  submissionData.errors.collectionDate
                }
                onChange={(date) => submissionData.setFieldValue('collectionDate', date.toString())}
              />
              <Input
                label={t('amount_deposited')}
                onChange={(obj) => {
                  // submissionData.handleChange
                  submissionData.setFieldValue('depositedAmount', obj.target.value);
                  const tempValue = parseFloat(obj.target.value, 10);
                  submissionData.setFieldValue('adjustment', (-1 * (parseFloat(submissionData.values.collectedAmount) - tempValue)).toFixed(2));
                }}
                value={submissionData?.values.depositedAmount}
                disabled={submissionData?.values?.isReadOnly || submissionLoading}
                required
                min={0}
                step={1}
                type="number"
                name="depositedAmount"
                id="depositedAmount"
                error={
                  submissionData.errors.depositedAmount &&
                  submissionData.touched.depositedAmount &&
                  submissionData.errors.depositedAmount
                }
              />
              <DateSelector
                label={t('deposit_date')}
                maxDate={dayjs().toDate()}
                value={Date.parse(submissionData.values.depositDate)}
                disabled={submissionData.values.isReadOnly || submissionLoading}
                error={
                  submissionData.errors.depositDate &&
                  submissionData.touched.depositDate &&
                  submissionData.errors.depositDate
                }
                onChange={(date) => submissionData.setFieldValue('depositDate', date.toString())}
              />
              <Input
                label={t('adjustment')}
                value={submissionData?.values.adjustment}
                disabled
                className="md:col-span-2"
                type="number"
                name="adjustment"
                id="adjustment"
              />
              <Input
                label={t('deposited_by')}
                onChange={submissionData.handleChange}
                value={submissionData?.values.depositedBy}
                disabled={submissionData?.values?.isReadOnly || submissionLoading}
                required
                className="md:col-span-2"
                type="completeName"
                name="depositedBy"
                id="depositedBy"
                error={
                  submissionData.errors.depositedBy &&
                  submissionData.touched.depositedBy &&
                  submissionData.errors.depositedBy
                }
              />
              <Input
                label={t('comments')}
                onChange={submissionData.handleChange}
                value={submissionData?.values.comments}
                disabled={submissionData?.values?.isReadOnly || submissionLoading}
                className="md:col-span-2"
                type="text"
                name="comments"
                id="comments"
                error={
                  submissionData.errors.comments &&
                  submissionData.touched.comments &&
                  submissionData.errors.comments
                }
              />
            </>
          )}
          {submissionData?.values.id && (
            <div className="md:col-span-2">
              <div className="h-divider bg-gray-300 mb-4" />
              {!submissionData?.values?.isReadOnly && (
                <>
                  <div>
                    <Dragger className="h-28" disabled={uploadingFile} {...draggerProps}>
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">
                        {t('click_or_drag_content')}
                      </p>
                    </Dragger>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 mt-2 gap-2">
                    <Select
                      label={t('file_type')}
                      className="md:col-span-2"
                      id="fileType"
                      name="fileType"
                      disabled={uploadingFile}
                      value={selectedFileType}
                      onChange={(obj) => setSelectedFileType(parseInt(obj.target.value, 10))}
                      options={_.map(fareboxCollectionSubmissionAttachmentFileTypes, (ft) => ({
                          key: ft.id,
                          value: ft.id,
                          text: t(ft.name) }))}
                    />
                    <CustomButton
                      className="bg-primaryColor px-4"
                      textColor="text-primaryTextColor"
                      loaderColor="bg-primaryTextColor"
                      borderColor="border-primaryBorderColor"
                      loading={uploadingFile}
                      disabled={!fileData}
                      text={t('upload')}
                      onClick={() => uploadFile()}
                    />
                  </div>
                </>
              )}

              <H4 className="mt-4" text={t('attachments')} />
              {_.map(attachments, (attachment) => (
                <div className="flex">
                  <Link className="text-lg" target="_blank" href={attachment?.attachmentUrl}>
                    • {t(fareboxCollectionSubmissionAttachmentFileTypesToString(
                        attachment.fileType))} - {attachment?.fileName}
                  </Link>
                  <Popconfirm
                    key="deleteattachment"
                    title={t('delete_attachment_prompt')}
                    okText={t('yes')}
                    cancelText={t('no')}
                    onConfirm={() => deleteFile(attachment?.id)}
                  >
                    <Button
                      size="small"
                      type="primary"
                      className="ml-2 mt-1 text-red-600 hover:text-white bg-transparent hover:bg-red-600"
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-xs" />
                    </Button>
                  </Popconfirm>
                </div>
              ))}
              {attachments?.length === 0 && (
                <p className="italic text-gray-400">{t('no_attachments')}</p>
              )}

              {submissionData?.values?.reviewer && (
                <>
                  <div className="h-divider bg-gray-300 mt-4" />
                  <H4 className="mt-4" text={t('review')} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="space-x-1 flex">
                      <strong className="mt-1">{t('reviewed_by')}:</strong>
                      <Avatar src={submissionData?.values?.reviewer?.avatar} />
                      <div className="mt-1">{submissionData?.values?.reviewer?.completeName}</div>
                    </div>
                    <div className="flex space-x-1">
                      <strong className="mt-1">{t('status')}:</strong>
                      <p className="mt-1">
                        {t(fareboxCollectionSubmissionStatusToString(
                          submissionData?.values.status))}
                      </p>
                    </div> 
                    <div className="md:col-span-2">
                      <strong>{t('reviewer_comments')}:</strong>
                      {submissionData?.values.reviewerComments && (
                        <p>{submissionData?.values.reviewerComments}</p>
                      )}
                      {!submissionData?.values.reviewerComments && (
                        <p className="italic text-gray-400">{t('no_comments')}</p>
                      )}
                    </div> 
                  </div>
                </>
              )}
            </div>
          )}
        </Form>
      </Modal>
      <Modal
        key="selectedSubmissionModalForReview"
        title={t('review_submission')}
        open={submissionDataForReview?.values?.openModal}
        onCancel={() => submissionDataForReview.setFieldValue('openModal', false)}
        centered
        footer={[
          <div className="flex space-x-2">
            <div className="mr-auto" />
            <CustomButton
              key="closeSelectedSubmissionModal"
              type="primary"
              danger
              className="bg-rose-500 px-4"
              textColor="text-primaryTextColor"
              loaderColor="bg-primaryTextColor"
              disabled={submissionLoading}
              onClick={() => submissionDataForReview.setFieldValue('openModal', false)}
              text={t('cancel')}
            />
            <CustomButton
              className="bg-primaryColor px-4"
              textColor="text-primaryTextColor"
              loaderColor="bg-primaryTextColor"
              borderColor="border-primaryBorderColor"
              loading={submissionLoading}
              text={t('submit')}
              onClick={submissionDataForReview.submitForm}
            />
          </div>
        ]}
      >
        <Form className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full overflow-y-auto">
          <div className="md:col-span-2 space-x-1 flex">
            <strong className="mt-1">{t('submitted_by')}:</strong>
            <Avatar src={submissionDataForReview?.values?.submitter?.avatar} />
            <div className="mt-1">{submissionDataForReview?.values?.submitter?.completeName}</div>
          </div>
          <div className="md:col-span-2">
            <strong>{t('description')}</strong>
            <p>{submissionDataForReview?.values.description}</p>
          </div>
          <div>
            <strong>{t('amount_collected')}</strong>
            <p>{toCurrency(submissionDataForReview?.values.collectedAmount)}</p>
          </div>
          <div>
            <strong>{t('collection_date')}</strong>
            <p>{dayjs(submissionDataForReview?.values.collectionDate).format('MMM d, 2024')}</p>
          </div>
          <div>
            <strong>{t('amount_deposited')}</strong>
            <p>{toCurrency(submissionDataForReview?.values.depositedAmount)}</p>
          </div>
          <div>
            <strong>{t('deposit_date')}</strong>
            <p>{dayjs(submissionDataForReview?.values.depositDate).format('MMM d, 2024')}</p>
          </div>
          <div className="md:col-span-2">
            <strong>{t('deposited_by')}</strong>
            <p>{submissionDataForReview?.values.depositedBy}</p>
          </div>
          <div className="md:col-span-2">
            <strong>{t('comments')}</strong>
            <p>{submissionDataForReview?.values.comments}</p>
          </div>
          <div className="h-divider bg-gray-300 md:col-span-2" />
          <div className="md:col-span-2">
            <H4 text={t('attachments')} />
            {_.map(attachments, (attachment) => (
              <div className="flex">
                <Link className="text-lg" target="_blank" href={attachment?.attachmentUrl}>
                  • {t(fareboxCollectionSubmissionAttachmentFileTypesToString(
                      attachment.fileType))} - {attachment?.fileName}
                </Link>
              </div>
            ))}
          </div>
          <div className="h-divider bg-gray-300 mb-4 md:col-span-2" />
          <Select
            label={t('status')}
            className="md:col-span-2"
            id="status"
            name="status"
            disabled={uploadingFile}
            value={submissionDataForReview?.values.status}
            onChange={(obj) => submissionDataForReview.setFieldValue('status', parseInt(obj.target.value, 10))}
            options={_.map(_.filter(fareboxCollectionSubmissionStatus,
              (s) => s.value > 0), (ft) => ({
                key: ft.id,
                value: ft.id,
                text: t(ft.name) }))}
            error={
              submissionDataForReview.errors.status &&
              submissionDataForReview.touched.status &&
              submissionDataForReview.errors.status
            }
          />
          <Input
            label={t('reviewer_comments')}
            onChange={submissionDataForReview.handleChange}
            value={submissionDataForReview?.values.reviewerComments}
            disabled={submissionLoading}
            className="md:col-span-2"
            type="text"
            name="reviewerComments"
            id="reviewerComments"
            error={
              submissionDataForReview.errors.reviewerComments &&
              submissionDataForReview.touched.reviewerComments &&
              submissionDataForReview.errors.reviewerComments
            }
          />
        </Form>
      </Modal>
    </MainPage>
  );
}

export default FareboxCollectionSubmissions;
